<script>
import RadialProgressBar from 'vue-radial-progress';
import { counterMixin } from '@mixins/counter-mixin';
import { videoComputed, videoMethods, workoutComputed, workoutMethods } from '@/src/state/helpers';

export default {
  components: {
    RadialProgressBar,
  },
  props: {
    timeRemaining: {
      type: Number,
      required: true,
      default: 0,
    },
    originalTime: {
      type: Number,
      required: true,
      default: 0,
    },
    diameter: {
      type: Number,
      required: true,
      default: 0,
    },
    backgroundColor: {
      type: String,
      required: true,
      default: '#ffffff',
    },
    type: {
      type: String,
      required: true,
      default: '',
    },
    lineWidth: {
      type: Number,
      required: true,
      default: 10,
    },
    restCountDown: {
      type: Number,
      required: false,
      default: 0,
    },
    isClockwise: {
      type: Boolean,
      required: false,
    },
    tabata: {
      type: Boolean,
      default: false,
    },
    startColor: {
      type: String,
      default: '#55BCF5',
    },
    stopColor: {
      type: String,
      default: '#3E4FE2',
    },
  },
  mixins: [counterMixin],
  data() {
    return {};
  },
  computed: {
    ...videoComputed,
    ...workoutComputed,
    isPlaying() {
      return this.videoAction === 'play';
    },
    formattedTime() {
      return this.$_getFormattedSeconds(this.timeRemaining);
    },
    videoUrl() {
      return (
        this.currentExercise &&
        this.currentExercise.mediaAssets &&
        this.currentExercise.mediaAssets.coverVideo &&
        this.currentExercise.mediaAssets.coverVideo.url
      );
    },
    hasTabataFitnessType() {
      if (this.currentCircuit.fitness_types) {
        return this.currentCircuit.fitness_types.filter((e) => e.label.toLowerCase() === 'tabata').length > 0;
      } else {
        return false;
      }
    },
    isTabata() {
      if (this.hasTabataFitnessType) {
        return this.currentCircuit.tabata || this.hasTabataFitnessType;
      } else {
        return this.currentCircuit.tabata;
      }
    },
  },
  mounted() {},
  methods: {
    ...videoMethods,
    ...workoutMethods,
    playPause() {
      this.isPlaying ? this.setVideoAction('pause') : this.setVideoAction('play');
    },
    ding() {
      var audio = null;
      if (this.type === 'exercise' && this.isTabata) {
        audio = new Audio(require('@assets/audio/tabata_rest.wav'));
      } else if (this.type === 'rest' && this.isTabata) {
        audio = new Audio(require('@assets/audio/tabata_start_exe.wav'));
      } else {
        audio = new Audio(require('@assets/audio/ding-sound.wav'));
      }
      audio.play();
    },
  },
  watch: {
    restCountDown() {
      if (this.type === 'rest' && this.restCountDown === 0 && !this.skipRestPeriod) {
        this.ding();
      }
    },
    timeRemaining() {
      this.setCurrentExerciseSetTime(this.originalTime - this.timeRemaining);
      if (this.type === 'exercise' && this.timeRemaining === 1) {
        this.ding();
      }
    },
  },
};
</script>

<template>
  <RadialProgressBar
    :diameter="type === 'rest' ? diameter * 1.2 : diameter"
    :completed-steps="originalTime - timeRemaining"
    :total-steps="originalTime"
    :stopColor="stopColor"
    :startColor="startColor"
    :innerStrokeColor="backgroundColor"
    :strokeWidth="lineWidth"
    :innerStrokeWidth="lineWidth"
    :isClockwise="isClockwise"
  >
    <template v-if="type === 'exercise'">
      <p class="time">{{ formattedTime }}</p>
      <div class="play" @click="playPause">
        <BaseIcon name="play" class="play-icon" v-if="!isPlaying && isTimerPaused" />
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'pause-icon'" v-bind:svg-inline="''" v-if="isPlaying || !isTimerPaused" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M8.563 3.603h-3.1V16h3.1V3.603zm6.199 0h-3.1V16h3.1V3.603z" fill="#2B2D34"/></svg>
      </div>
    </template>
    <template v-if="type === 'rest'">
      <p class="rest-title">Starting in</p>
      <p class="rest-counter">{{ restCountDown }}</p>
    </template>
  </RadialProgressBar>
</template>

<style lang="scss" scoped>
@import '@design';

.radial-progress-container {
  margin-bottom: 2.25rem;

  .time {
    align-items: center;
    color: white;
    display: flex;
    font-family: $base-font-family;
    font-size: 2.6rem;
    font-style: normal;
    font-weight: 800;
    line-height: 3.5rem;
    margin-bottom: 0;
    text-align: center;
    text-transform: uppercase;

    @media screen and (min-width: 1100px) {
      @include calc-font-size(2.5);
    }
  }

  .rest-title {
    align-items: center;
    color: white;
    display: flex;
    font-family: $base-font-family;
    font-size: 2rem;
    font-style: normal;
    font-weight: 600;
    letter-spacing: 0.5px;
    line-height: 2.7rem;
    margin-bottom: 0;
    opacity: 0.8;
    text-align: center;

    @media screen and (min-width: 1100px) {
      @include calc-font-size(1.3);
    }
  }

  .rest-counter {
    color: white;
    font-family: $base-font-family;
    font-size: 7rem;
    font-style: normal;
    font-weight: bold;
    line-height: 6.4rem;
    margin-bottom: 0;
    text-align: center;
    text-transform: uppercase;
  }

  .play {
    align-items: center;
    background-color: white;
    border-radius: 50%;
    bottom: -10px;
    cursor: pointer;
    display: flex;
    height: 4.5rem;
    justify-content: center;
    left: auto;
    position: absolute;
    right: auto;
    transition: all 0.3s ease-in;
    width: 4.5rem;
    svg {
      transition: unset;
      vertical-align: unset;
      &.pause-icon {
        height: 2rem;
        margin-top: 2px;
        width: 2rem;
      }
      &.play-icon {
        height: 1.5rem;
        width: 1.5rem;
      }
      path {
        height: 100% !important;
        width: 100% !important;
      }
    }
    &:hover {
      background-color: black;
      svg {
        color: white;
        path {
          fill: white;
        }
      }
    }
  }
}
</style>
